import { publish } from '@grubhubprod/umami-library/dist/utilities/eventManager';
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import 'src/utilities/variants';
import { cloneDeep } from '@grubhubprod/umami-library/dist/nodash/cloneDeep';
import { getWindow } from 'src/utilities/window';

export interface IVariantsContext {
  overrideVariants: (variants: IVariants) => void;
  variantIsActive: (key: string) => boolean;
  cleanOverriddenVariants: () => void;
  combinedVariants: IVariants;
  hasOverrides: boolean;
}

interface IVariantsProviderProps {
  children: ReactNode;
}

export interface IVariant {
  selected: boolean;
  value?: unknown;
  page?: string;
  url?: string;
  description?: string;
  ticket?: string;
  createDate?: string;
  date?: Date;
  valueType: string;
  editValue: string;
  key: string;
  stakeholder: string;
  threeMonthsAgo?: boolean;
  sixMonthsAgo?: boolean;
}

export interface IVariants {
  [variantKey: string]: IVariant;
}

export interface IWindow extends Window {
  localVariants: IVariants;
}

// needs to match corp admin storage key
export const VARIANTS_LS_KEY = 'ngStorage-abPageVariants';

export enum VariantLocalStorageActions {
  CLEAN_UP = 'CLEAN_UP',
  GET = 'GET',
  SET = 'SET',
}

const VariantsContext = createContext<IVariantsContext>(null);

function VariantsProvider({ children }: IVariantsProviderProps): JSX.Element {
  const window = getWindow() as IWindow;
  const [combinedVariants, setCombinedVariants] = useState<IVariants>();

  const [hasOverrides, setHasOverrides] = useState<boolean>();

  useEffect(() => {
    buildAndNotifyObservers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function performVariantLocalStorageAction(
    action: VariantLocalStorageActions,
    value?: IVariants
  ): IVariants {
    try {
      switch (action) {
        case VariantLocalStorageActions.GET: {
          const variants = window.localStorage.getItem(VARIANTS_LS_KEY);
          if (variants) {
            return JSON.parse(variants);
          }
          break;
        }
        case VariantLocalStorageActions.SET:
          window.localStorage.setItem(VARIANTS_LS_KEY, JSON.stringify(value));
          break;
        case VariantLocalStorageActions.CLEAN_UP:
          window.localStorage.removeItem(VARIANTS_LS_KEY);
          buildAndNotifyObservers();
          break;
        default:
          return JSON.parse(window.localStorage.getItem(VARIANTS_LS_KEY));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`Unable to perform ${action} action on localStorage${err}`);
    }
    return null;
  }

  function getDefaultVariants(): IVariants {
    return window.localVariants;
  }

  function variantIsActive(variantId: string): boolean {
    return (
      combinedVariants &&
      !!combinedVariants[variantId] &&
      combinedVariants[variantId].selected
    );
  }

  function overrideVariants(variants: IVariants): void {
    const currentAbSettings =
      performVariantLocalStorageAction(VariantLocalStorageActions.GET) || {};
    const variantsToSet = { ...currentAbSettings, ...variants };
    performVariantLocalStorageAction(
      VariantLocalStorageActions.SET,
      variantsToSet
    );
    buildAndNotifyObservers();
  }

  function cleanOverriddenVariants() {
    performVariantLocalStorageAction(VariantLocalStorageActions.CLEAN_UP);
  }

  function buildAndNotifyObservers(): void {
    buildActiveVariants();
    publish('$updateVariants');
  }

  function buildActiveVariants() {
    const localVariants = performVariantLocalStorageAction(
      VariantLocalStorageActions.GET
    );
    setCombinedVariants({
      ...cloneDeep(getDefaultVariants()),
      ...localVariants,
    });
    setHasOverrides(!!localVariants);
  }

  return (
    <VariantsContext.Provider
      value={{
        overrideVariants,
        variantIsActive,
        combinedVariants,
        hasOverrides,
        cleanOverriddenVariants,
      }}
    >
      {children}
    </VariantsContext.Provider>
  );
}

const useVariants = (): IVariantsContext => {
  const context = useContext(VariantsContext);

  if (!context) {
    throw new Error('useVariant must be used within a VariantsContext');
  }
  return context;
};

export { VariantsProvider, useVariants };
